/**
 * Thin wrapper around window.CubeConnector from cube-connector-v2.js
 * (index.html + environment.cube_connector_script). One instance is shared by
 * the mt-calling widget and the debug panel — do not create a second connector.
 */
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { getCubeConnectorChildOrigin, getCubeConnectorScriptUrl } from '../cube/cube-dialer-mode';

export type CubeSdkAgent = {
  agentId: string;
  extension: string;
  password: string;
  campaignId: string;
};

export type CubeSdkCallbacks = {
  onReady?: (data: unknown) => void;
  onLoggedIn?: (data: unknown) => void;
  onLoginFailed?: (data: unknown) => void;
  onLoggedOut?: (data: unknown) => void;
  onInboundCall?: (data: unknown) => void;
  onCallAnswered?: (data: unknown) => void;
  onCallEnded?: (data: unknown) => void;
  onCallFailed?: (data: unknown) => void;
  onBreakStarted?: (data: unknown) => void;
  onBreakEnded?: (data: unknown) => void;
  onMessage?: (data: unknown) => void;
  onError?: (data: unknown) => void;
  onIframeLoad?: (data: unknown) => void;
};

type CubeSdkConnector = {
  setAgent(agent: CubeSdkAgent): void;
  login(): boolean;
  logout(): boolean;
  makeCall(number: string): boolean;
  answer(): boolean;
  hangup(): boolean;
  hold(): boolean;
  unhold(): boolean;
  mute(): boolean;
  unmute(): boolean;
  transfer(number: string): boolean;
  redirect(number: string): boolean;
  breakStart(breakInfo?: string): boolean;
  breakEnd(): boolean;
  dispose(disposition?: string, callbackDateTime?: string | null, mustTransfer?: string): boolean;
  destroy(): void;
};

type CubeConnectorCtor = new (iframeId: string, options?: Record<string, unknown>) => CubeSdkConnector;

@Injectable({ providedIn: 'root' })
export class CubeSdkService {
  private connector: CubeSdkConnector | null = null;

  readonly ready$ = new Subject<unknown>();
  readonly loggedIn$ = new Subject<unknown>();
  readonly loginFailed$ = new Subject<unknown>();
  readonly loggedOut$ = new Subject<unknown>();
  readonly inboundCall$ = new Subject<unknown>();
  readonly callAnswered$ = new Subject<unknown>();
  readonly callEnded$ = new Subject<unknown>();
  readonly callFailed$ = new Subject<unknown>();
  readonly breakStarted$ = new Subject<unknown>();
  readonly breakEnded$ = new Subject<unknown>();
  readonly message$ = new Subject<unknown>();
  readonly error$ = new Subject<unknown>();
  readonly iframeLoad$ = new Subject<unknown>();

  isInitialized(): boolean {
    return !!this.connector;
  }

  isReady(): boolean {
    return this.isInitialized();
  }

  /** Creates CubeConnector once. Later calls are no-ops so widget + debug stay on the same session. */
  async init(iframeId: string, callbacks: CubeSdkCallbacks = {}): Promise<void> {
    await this.ensureScript();
    const Ctor = (window as unknown as { CubeConnector?: CubeConnectorCtor }).CubeConnector;
    if (!Ctor) {
      throw new Error('CubeConnector SDK not available');
    }
    await this.waitForIframe(iframeId);
    if (this.connector) {
      return;
    }
    const childOrigin = getCubeConnectorChildOrigin();
    this.connector = new Ctor(iframeId, {
      childOrigin,
      onReady: (data: unknown) => this.emit('onReady', this.ready$, callbacks.onReady, data),
      onLoggedIn: (data: unknown) => this.emit('onLoggedIn', this.loggedIn$, callbacks.onLoggedIn, data),
      onLoginFailed: (data: unknown) =>
        this.emit('onLoginFailed', this.loginFailed$, callbacks.onLoginFailed, data),
      onLoggedOut: (data: unknown) => this.emit('onLoggedOut', this.loggedOut$, callbacks.onLoggedOut, data),
      onInboundCall: (data: unknown) =>
        this.emit('onInboundCall', this.inboundCall$, callbacks.onInboundCall, data),
      onCallAnswered: (data: unknown) =>
        this.emit('onCallAnswered', this.callAnswered$, callbacks.onCallAnswered, data),
      onCallEnded: (data: unknown) => this.emit('onCallEnded', this.callEnded$, callbacks.onCallEnded, data),
      onCallFailed: (data: unknown) => this.emit('onCallFailed', this.callFailed$, callbacks.onCallFailed, data),
      onBreakStarted: (data: unknown) =>
        this.emit('onBreakStarted', this.breakStarted$, callbacks.onBreakStarted, data),
      onBreakEnded: (data: unknown) => this.emit('onBreakEnded', this.breakEnded$, callbacks.onBreakEnded, data),
      onMessage: (data: unknown) => this.emit('onMessage', this.message$, callbacks.onMessage, data),
      onError: (data: unknown) => this.emit('onError', this.error$, callbacks.onError, data),
      onIframeLoad: (data: unknown) => this.emit('onIframeLoad', this.iframeLoad$, callbacks.onIframeLoad, data),
    });
    console.log('[CubeSDK] CubeConnector created', { iframeId, childOrigin });
  }

  setAgent(agent: CubeSdkAgent | Record<string, string>): boolean {
    if (!this.connector) {
      return false;
    }
    const payload = this.toSdkAgent(agent);
    console.log('[CubeSDK] setAgent()', {
      agentId: payload.agentId,
      extension: payload.extension,
      campaignId: payload.campaignId,
    });
    this.connector.setAgent(payload);
    return true;
  }

  login(): boolean {
    return this.invoke('login');
  }

  logout(): boolean {
    return this.invoke('logout');
  }

  makeCall(number: string): boolean {
    return this.invoke('makeCall', [number]);
  }

  answer(): boolean {
    return this.invoke('answer');
  }

  hangup(): boolean {
    return this.invoke('hangup');
  }

  hold(): boolean {
    return this.invoke('hold');
  }

  unhold(): boolean {
    return this.invoke('unhold');
  }

  mute(): boolean {
    return this.invoke('mute');
  }

  unmute(): boolean {
    return this.invoke('unmute');
  }

  transfer(number: string): boolean {
    return this.invoke('transfer', [number]);
  }

  redirect(number: string): boolean {
    return this.invoke('redirect', [number]);
  }

  breakStart(breakInfo?: string): boolean {
    return this.invoke('breakStart', [breakInfo]);
  }

  breakEnd(): boolean {
    return this.invoke('breakEnd');
  }

  dispose(
    disposition?: string,
    callbackDateTime: string | null = null,
    mustTransfer = '0'
  ): boolean {
    return this.invoke('dispose', [disposition, callbackDateTime, mustTransfer]);
  }

  setAutoAnswer(_enabled: boolean): boolean {
    return false;
  }

  destroy(): void {
    if (!this.connector) {
      return;
    }
    try {
      this.connector.logout();
    } catch (_e) {}
    try {
      this.connector.destroy();
    } catch (_e) {}
    this.connector = null;
  }

  private toSdkAgent(agent: CubeSdkAgent | Record<string, string>): CubeSdkAgent {
    const row = agent as Record<string, string>;
    return {
      agentId: String(row['agentId'] || row['agentID'] || '').trim(),
      extension: String(row['extension'] || row['agentExtn'] || '').trim(),
      password: String(row['password'] || ''),
      campaignId: String(row['campaignId'] || row['campaignID'] || row['CampaignId'] || '').trim(),
    };
  }

  private emit(
    event: string,
    subject: Subject<unknown>,
    callback: ((data: unknown) => void) | undefined,
    data: unknown
  ): void {
    console.log('[CubeSDK] CALLBACK', event, data);
    subject.next(data);
    callback?.(data);
  }

  private invoke(method: string, args: unknown[] = []): boolean {
    const connector = this.connector as unknown as Record<string, unknown> | null;
    if (!connector || typeof connector[method] !== 'function') {
      console.warn('[CubeSDK]', method + '() not available');
      return false;
    }
    try {
      const result = (connector[method] as (...a: unknown[]) => unknown).apply(connector, args);
      console.log('[CubeSDK]', method + '()', args.length ? args : '', result);
      return result !== false;
    } catch (e) {
      console.error('[CubeSDK]', method + '() exception', e);
      return false;
    }
  }

  /** Prefer the script already in index.html; inject only if CubeConnector is missing. */
  private async ensureScript(): Promise<void> {
    if ((window as unknown as { CubeConnector?: CubeConnectorCtor }).CubeConnector) {
      return;
    }
    const src = getCubeConnectorScriptUrl();
    if (!document.querySelector(`script[src="${src}"]`)) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.body.appendChild(script);
      });
    }
    await this.waitForCtor();
  }

  private waitForCtor(timeoutMs = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        if ((window as unknown as { CubeConnector?: CubeConnectorCtor }).CubeConnector) {
          resolve();
          return;
        }
        if (Date.now() - started > timeoutMs) {
          reject(new Error('CubeConnector SDK not available'));
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  private waitForIframe(id: string, timeoutMs = 15000): Promise<HTMLIFrameElement> {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        const el = document.getElementById(id) as HTMLIFrameElement | null;
        if (el) {
          resolve(el);
          return;
        }
        if (Date.now() - started > timeoutMs) {
          reject(new Error(`Iframe #${id} not found in DOM`));
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }
}
